import "./AddPartners.scss";
import { useEffect, useState } from "react";
import {
  InputAdornment,
  TextField,
  useMediaQuery,
  Modal,
  Box,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AddPartnersColumns } from "../../SourceData";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/ClearOutlined";
import AcceptIcon from "@mui/icons-material/CheckOutlined";
import CloseIcon from "@mui/icons-material/Close";
import WhatsApp from "./../../assets/whats-icon.svg";
import ViewIcon from "./../../assets/show-user.svg";
import LinkIcon from "./../../assets/linkicon.png";
import api from "../../utils/api";
import MessageModals from "../../components/MessageModals/MessageModals";
import BlueButton from "../../components/BlueButton/BlueButton";
import usePermissions from "../../hooks/usePermissions";

const AddPartners = () => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const [query, setQuery] = useState("");
  const [openMessage, setOpenMessage] = useState(false);
  const [currentView, setCurrentView] = useState("");
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const { hasPermission } = usePermissions();
  // Added state for app percentage modal
  const [appPercentage, setAppPercentage] = useState("");
  const [openAppPercentageModal, setOpenAppPercentageModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // New state for ID image modal
  const [openIdModal, setOpenIdModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  useEffect(() => {
    getData();
  }, [query, page, pageSize]);

  const getData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page + 1,
        limit: pageSize,
      });

      if (query) {
        params.append("search", query);
      }

      const res = await api.get(`/register-vendors?${params.toString()}`);
      setVendors(res.data.data);
      // Fix: Use the total count from API response, not the current page data length
      setTotalRows(res.data.total); // This should be 12 in your example
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMessage = (view, vendorId) => {
    setSelectedVendorId(vendorId);
    setCurrentView(view);

    // If accepting a vendor, show the app percentage modal first
    if (view === "accept") {
      setOpenAppPercentageModal(true);
    } else {
      setOpenMessage(true);
    }
  };

  // Handle pagination changes
  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  const handleModalClose = () => {
    setOpenMessage(false);
    setSelectedVendorId(null);
  };

  // Handle app percentage modal close
  const handleAppPercentageModalClose = () => {
    setOpenAppPercentageModal(false);
    setAppPercentage("");
    setSelectedVendorId(null);
  };

  // Handle app percentage submission
  const handleAppPercentageSubmit = async () => {
    if (!appPercentage) {
      toast.error("يرجى إدخال نسبة التطبيق");
      return;
    }

    setIsSubmitting(true);
    try {
      // Submit vendor approval with app percentage
      const response = await api.post("/action-register-vendor", {
        vendor_id: selectedVendorId,
        active: "approve",
        app_percentage: appPercentage,
      });

      if (response.status === 200 || response.status === 201) {
        toast.success("تم تحديث حالة الطلب والنسبة بنجاح");
        setOpenAppPercentageModal(false);
        setAppPercentage("");
        getData(); // Refresh the data
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "حدث خطأ أثناء العملية");
    } finally {
      setIsSubmitting(false);
    }
  };

  // New handler for opening the ID images modal
  const handleOpenIdModal = (vendor) => {
    setSelectedVendor(vendor);
    setOpenIdModal(true);
  };

  // New handler for closing the ID images modal
  const handleCloseIdModal = () => {
    setOpenIdModal(false);
    setSelectedVendor(null);
  };

  const idModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMobile ? "90%" : "70%",
    maxWidth: "900px",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: "8px",
    maxHeight: "90vh",
    overflow: "auto",
  };

  // Style for app percentage modal
  const appPercentageModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMobile ? "90%" : "400px",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "8px",
    padding: 0,
    overflow: "hidden",
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "",
      width: 240,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="cell-action">
            {hasPermission(44) && (
              <>
                <div
                  className="icon-container"
                  style={{ backgroundColor: "#E63050", color: "white" }}
                  onClick={() => handleMessage("reject", params.row.id)}
                >
                  <CancelIcon className="icon" style={{ color: "white" }} />
                </div>

                <div
                  className="icon-container"
                  style={{ backgroundColor: "#28B446" }}
                  onClick={() => handleMessage("accept", params.row.id)}
                >
                  <AcceptIcon className="icon" style={{ color: "white" }} />
                </div>
              </>
            )}

            <Link className="cell-btn" to={params?.row?.url} target="_blank">
              <img src={LinkIcon} alt="" style={{ padding: "5px" }} />
            </Link>

            <Link
              className="cell-btn"
              to={`https://wa.me/${params?.row?.whatsapp}`}
              target="_blank"
            >
              <img src={WhatsApp} alt="" />
            </Link>
            {hasPermission(43) && (
              <div
                className="cell-btn"
                onClick={() => handleOpenIdModal(params.row)}
                style={{ cursor: "pointer" }}
              >
                <img src={ViewIcon} alt="" />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="users">
      <div className="special-header">
        <p className="title">طلبات تسجيل المتاجر/المصانع</p>
        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="البحث باسم المتجر/ المصنع"
              onChange={(e) => setQuery(e.target.value)}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </div>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={AddPartnersColumns.concat(actionColumn)}
          rows={vendors.map((row, index) => ({
            ...row,
            index: page * pageSize + index + 1, // Fix: Calculate global index
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          paginationMode="server" // Add this for server-side pagination
          pageSizeOptions={[10, 25, 50]}
          paginationModel={{
            page,
            pageSize,
          }}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={totalRows}
          getRowId={(row) => row.id}
        />
      </div>

      {/* App Percentage Modal */}
      <Modal
        open={openAppPercentageModal}
        onClose={handleAppPercentageModalClose}
        aria-labelledby="app-percentage-modal"
        aria-describedby="modal-to-set-app-percentage"
      >
        <Box sx={appPercentageModalStyle}>
          <div
            className="container"
            style={{ justifyContent: "space-between", padding: "24px 10px" }}
          >
            <div className="header" onClick={handleAppPercentageModalClose}>
              <CloseIcon style={{ cursor: "pointer" }} />
            </div>

            <h3>نسبة التطبيق</h3>
            <FormControl
              sx={{ width: "100%", padding: "0px", marginTop: "20px" }}
              variant="outlined"
            >
              <InputLabel
                htmlFor="outlined-adornment-amount"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                المبلغ
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="outlined-adornment-amount"
                type="number"
                notched
                placeholder="%"
                value={appPercentage}
                onChange={(e) => setAppPercentage(e.target.value)}
              />
            </FormControl>

            <div className="footer" style={{ marginTop: "20px" }}>
              <BlueButton
                text="تاكيد"
                onClick={handleAppPercentageSubmit}
                size="medium"
                variant="fill"
                loading={isSubmitting}
              />
            </div>
          </div>
        </Box>
      </Modal>

      {/* Original Message Modal for rejection cases */}
      <MessageModals
        open={openMessage}
        currentView={currentView}
        onClose={handleModalClose}
        itemId={selectedVendorId}
        onDataChange={getData}
        text="تم تحديث حالة الطلب بنجاح"
        acceptRoute="/action-register-vendor"
        rejectRoute="/action-register-vendor"
        acceptData={{
          vendor_id: selectedVendorId,
          active: "approve",
        }}
        rejectData={{
          vendor_id: selectedVendorId,
          active: "reject",
          app_percentage: 0,
        }}
        type="vendor"
      />

      {/* New Modal for ID Images */}
      <Modal
        open={openIdModal}
        onClose={handleCloseIdModal}
        aria-labelledby="id-images-modal"
        aria-describedby="modal-to-display-id-images"
      >
        <Box sx={idModalStyle} dir="rtl">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <Typography variant="h6" component="h2" fontWeight="bold">
              {selectedVendor?.name} - بطاقة الهوية
            </Typography>
            <div onClick={handleCloseIdModal} style={{ cursor: "pointer" }}>
              <CloseIcon />
            </div>
          </div>

          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: "20px",
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle1" component="p" mb={2}>
                صورة الوجه
              </Typography>
              {selectedVendor?.id_front_image && (
                <img
                  src={selectedVendor.id_front_image}
                  alt="صورة الوجه"
                  style={{
                    width: "100%",
                    maxHeight: "300px",
                    minHeight: "300px",
                    objectFit: "contain",
                    border: "1px solid #eee",
                    borderRadius: "8px",
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle1" component="p" mb={2}>
                صورة الظهر
              </Typography>
              {selectedVendor?.id_back_image && (
                <img
                  src={selectedVendor.id_back_image}
                  alt="صورة الظهر"
                  style={{
                    width: "100%",
                    maxHeight: "300px",
                    minHeight: "300px",
                    objectFit: "contain",
                    border: "1px solid #eee",
                    borderRadius: "8px",
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default AddPartners;
